import { InspectionModel } from '../../data/types';
import { inspectionRecordService } from '../../services/inspection/inspectionRecordService';
import observationImagesHelpers from '../../helpers/observationImagesHelpers';
import {
  isCountedFailure,
  MissingPhotoError,
} from '../../helpers/sendFailureHelpers';
import { uploadUnsyncedImages } from './uploadUnsyncedImages';
import type {
  PhotoDeliveryOutcome,
  PhotoProgressListener,
} from '../../services/inspectionSync/photoDeliveryCore';

/**
 * Uploads an Inspection's photos that the server hasn't received, reporting progress.
 * Kept free of React so it is testable without the hook (SubmitScreenSkipPath.test
 * stubs the useImageSync MODULE, and under the bun cross-file mock leak that stub
 * would win). It never finishes delivery: photoDeliveryService does that, once per
 * Inspection, when this returns no failures.
 */
export async function syncInspectionImages(
  working: InspectionModel,
  { onProgress, onByteProgress }: PhotoProgressListener,
): Promise<PhotoDeliveryOutcome> {
  const unsyncedImageObservations =
    await observationImagesHelpers.getUnsyncedImageObservations(
      working.inspectionCategoryTypes.flatMap(
        (categoryType) => categoryType.observations,
      ),
    );

  if (unsyncedImageObservations.length === 0) {
    onProgress?.({ total: 1, loaded: 1, status: 'completed' });
    onByteProgress?.({ loaded: 0, total: 0 });
    return { failures: [], total: 0 };
  }

  const total = unsyncedImageObservations.length;
  onProgress?.({ total, loaded: 0, status: 'uploading' });
  const gaveUpOn: unknown[] = [];

  try {
    const { uploadedCount, failures } = await uploadUnsyncedImages(
      unsyncedImageObservations,
      {
        // Drop byte events without a real total: ApiHelpers.uploadImage emits a
        // (0, 0) event from xhr.onload/onerror BEFORE the upload promise resolves,
        // and forwarding it zeroed the bar's byte fraction while markSynced was
        // still running - a visible dip on every photo (#10549). The fraction is
        // reset deliberately in onPhotoDone below, together with the count.
        upload: async (obs) => {
          if (!obs.hasImage) throw new MissingPhotoError(obs.value);
          await observationImagesHelpers.uploadObservationImage(
            obs,
            (bytes) => {
              if (bytes.total > 0) {
                onByteProgress?.(bytes);
              }
            },
          );
        },
        markSynced: (obs) =>
          inspectionRecordService.recordPhotoReceived(working.externalId, obs),
        onPhotoDone: (loaded) => {
          // Reset the per-image byte fraction IN THE SAME callback as the photo-count
          // update: the finished image's final byte event left the fraction at 1, and
          // the bar (processed + byteFraction) / total counted it on top of the
          // incremented count - overshooting a full image, then snapping back when the
          // next image's first byte event reset the fraction (#10549). Emitting both
          // together renders one consistent step instead of the bounce.
          onByteProgress?.({ loaded: 0, total: 0 });
          onProgress?.({ total, loaded, status: 'uploading' });
        },
        onGaveUp: (_obs, error) => gaveUpOn.push(error),
      },
    );

    // Failed photos stay unsynced and retry on the next sync trigger.
    onProgress?.(
      failures.length > 0
        ? { total, loaded: uploadedCount, status: 'error' }
        : { total, loaded: total, status: 'completed' },
    );
    if (failures.length === 0) return { failures, total };
    const lastError = gaveUpOn[gaveUpOn.length - 1];
    return {
      failures,
      total,
      failure: {
        counted: gaveUpOn.some(isCountedFailure),
        reason:
          lastError instanceof Error
            ? lastError.message
            : `${failures.length} photo(s) could not be sent.`,
      },
    };
  } catch (err) {
    // uploadUnsyncedImages catches per-photo errors, so this only fires on an
    // unexpected top-level failure (e.g. a callback throwing) - report no progress.
    onProgress?.({ total, loaded: 0, status: 'error' });
    throw err;
  } finally {
    onByteProgress?.({ loaded: 0, total: 0 });
  }
}
